import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import { SelectInput, isNumber } from '../Inputs/Inputs'
import styles from '../../../../css/settings.module.css'

const Autostart = ({ devices, intl: { formatMessage } }) => {
  // Initialize device autostart settings
  const initializeDeviceAutostartSettings = () => {
    const deviceAutostartSettings = []
    let speedTest = 0

    for (let i = 0; i < devices.length; i += 1) {
      const {
        address,
        interface_versioned,
      } = devices[i]

      const {
        data: {
          other: {
            controller = 'N/A',
          } = { },
        } = { },
      } = interface_versioned || {}

      deviceAutostartSettings.push({
        address,
        status: 'new',
        settings: {},
      })

      if (controller === 'mpc20_rpmControl') speedTest += 1
    }

    return {
      deviceAutostartSettings,
      speedEnabled: devices.length === speedTest,
    }
  }

  const { deviceAutostartSettings: initialDeviceAutostartSettings, speedEnabled: initialSpeedEnabled } = initializeDeviceAutostartSettings()

  const [auto, setAuto] = useState(null)
  const [run, setRun] = useState(null)
  const [speed, setSpeed] = useState(null)
  const [deviceAutostartSettings, setDeviceAutostartSettings] = useState(initialDeviceAutostartSettings)
  const [autoInvalid, setAutoInvalid] = useState(false)
  const [runInvalid, setRunInvalid] = useState(false)
  const [speedInvalid, setSpeedInvalid] = useState(false)
  const [speedEnabled] = useState(initialSpeedEnabled)

  const appDispatch = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: deviceAutostartSettings,
    })
    AppDispatcher.dispatch({
      action: 'invalid-setting',
      invalid: autoInvalid || runInvalid || speedInvalid,
    })
  }, [deviceAutostartSettings, autoInvalid, runInvalid, speedInvalid])

  useEffect(() => {
    appDispatch()
  }, [deviceAutostartSettings, autoInvalid, runInvalid, speedInvalid])

  const handleChange = useCallback((field, value, callback) => {
    let newAutoInvalid = autoInvalid
    let newRunInvalid = runInvalid
    let newSpeedInvalid = speedInvalid

    const newDeviceAutostartSettings = []
    const valueNumber = Number(value)
    const valueCorrect = isNumber(valueNumber)

    for (let i = 0; i < deviceAutostartSettings.length; i += 1) {
      newDeviceAutostartSettings.push(deviceAutostartSettings[i])
      if (field === 'auto') {
        if (!valueCorrect || value > 1 || value < -1) {
          newAutoInvalid = true
        } else {
          newAutoInvalid = false
        }

        switch (valueNumber) {
          case 0:
            newDeviceAutostartSettings[i].settings.auto = 0
            break
          case 1:
            newDeviceAutostartSettings[i].settings.auto = 1
            break
          default:
            if (
              newDeviceAutostartSettings[i].settings.auto ||
              newDeviceAutostartSettings[i].settings.auto === 0
            ) {
              delete newDeviceAutostartSettings[i].settings.auto
            }
            break
        }
      } else if (field === 'run') {
        if (!valueCorrect || value > 1 || value < -1) {
          newRunInvalid = true
        } else {
          newRunInvalid = false
        }

        switch (valueNumber) {
          case 0:
            newDeviceAutostartSettings[i].settings.run = 0
            break
          case 1:
            newDeviceAutostartSettings[i].settings.run = 1
            break
          default:
            if (
              newDeviceAutostartSettings[i].settings.run ||
              newDeviceAutostartSettings[i].settings.run === 0
            ) {
              delete newDeviceAutostartSettings[i].settings.run
            }
            break
        }
      } else if (field === 'speed') {
        if (!valueCorrect || value > 2800 || value < 1800) {
          newSpeedInvalid = true
        } else {
          newSpeedInvalid = false
        }

        if (value <= 2800 && value >= 1800) {
          newDeviceAutostartSettings[i].settings.speed = Math.trunc(value)
        } else {
          if (
            newDeviceAutostartSettings[i].settings.speed ||
            newDeviceAutostartSettings[i].settings.speed === 0
          ) {
            delete newDeviceAutostartSettings[i].settings.speed
          }
        }
      }
    }

    // Update state
    if (field === 'auto') {
      setAuto(value)
      setAutoInvalid(newAutoInvalid)
    } else if (field === 'run') {
      setRun(value)
      setRunInvalid(newRunInvalid)
    } else if (field === 'speed') {
      setSpeed(value)
      setSpeedInvalid(newSpeedInvalid)
    }

    setDeviceAutostartSettings(newDeviceAutostartSettings)
  }, [deviceAutostartSettings, autoInvalid, runInvalid, speedInvalid, appDispatch])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [handleChange])

  let autoStyle = styles.autostartSelectInput
  let runStyle = styles.autostartSelectInput
  let speedStyle = styles.autostartSelectInput

  if (autoInvalid) {
    autoStyle = `${styles.autostartSelectInput} ${styles.inputIssue}`
  }
  if (runInvalid) {
    runStyle = `${styles.autostartSelectInput} ${styles.inputIssue}`
  }
  if (speedInvalid) {
    speedStyle = `${styles.autostartSelectInput} ${styles.inputIssue}`
  }

  return (
    <div className={`${styles.inputArea} ${styles.paddedInputArea}`}>
      <section>
        <FormattedMessage
          id='device.controllerState'
          defaultMessage='Controller State'
          description='Controller State label'
          tagName='h5'
        />
        <div className={styles.autostartSelect}>
          <SelectInput
            value={auto}
            field='auto'
            childChange={childChange}
            options={[
              {
                value: -1,
                name: formatMessage({
                  id: 'setting.noChange',
                  defaultMessage: 'No Change',
                  description: '',
                }),
              },
              {
                value: 0,
                name: formatMessage({
                  id: 'setting.manual.normal',
                  defaultMessage: 'Manual',
                  description: '',
                }),
              },
              {
                value: 1,
                name: formatMessage({
                  id: 'setting.auto.normal',
                  defaultMessage: 'Auto',
                  description: '',
                }),
              },
            ]}
            style={autoStyle}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='device.machineState.name'
          defaultMessage='Machine State'
          description='Machine State label'
          tagName='h5'
        />
        <div className={styles.autostartSelect}>
          <SelectInput
            value={run}
            field='run'
            childChange={childChange}
            options={[
              {
                value: -1,
                name: formatMessage({
                  id: 'setting.noChange',
                  defaultMessage: 'No Change',
                  description: '',
                }),
              },
              {
                value: 0,
                name: formatMessage({
                  id: 'setting.engineStop',
                  defaultMessage: 'Engine Stop',
                  description: '',
                }),
              }
            ]}
            style={runStyle}
          />
        </div>
      </section>
      {speedEnabled
        ? (
          <section>
            <FormattedMessage
              id='device.rpmTarget.name'
              defaultMessage='RPM Target'
              description='RPM Target label'
              tagName='h5'
            />
            <div className={styles.autostartSelect}>
              <SelectInput
                value={speed}
                field='speed'
                childChange={childChange}
                options={[
                  {
                    value: -1,
                    name: formatMessage({
                      id: 'setting.noChange',
                      defaultMessage: 'No Change',
                      description: '',
                    }),
                  },
                  {
                    value: 1800,
                    name: '1800 RPM',
                  },
                  {
                    value: 1850,
                    name: '1850 RPM',
                  },
                  {
                    value: 1900,
                    name: '1900 RPM',
                  },
                  {
                    value: 1950,
                    name: '1950 RPM',
                  },
                  {
                    value: 2000,
                    name: '2000 RPM',
                  },
                  {
                    value: 2050,
                    name: '2050 RPM',
                  },
                  {
                    value: 2100,
                    name: '2100 RPM',
                  },
                  {
                    value: 2150,
                    name: '2150 RPM',
                  },
                  {
                    value: 2200,
                    name: '2200 RPM',
                  },
                  {
                    value: 2250,
                    name: '2250 RPM',
                  },
                  {
                    value: 2300,
                    name: '2300 RPM',
                  },
                  {
                    value: 2350,
                    name: '2350 RPM',
                  },
                  {
                    value: 2400,
                    name: '2400 RPM',
                  },
                  {
                    value: 2450,
                    name: '2450 RPM',
                  },
                  {
                    value: 2500,
                    name: '2500 RPM',
                  },
                  {
                    value: 2550,
                    name: '2550 RPM',
                  },
                  {
                    value: 2600,
                    name: '2600 RPM',
                  },
                  {
                    value: 2650,
                    name: '2650 RPM',
                  },
                  {
                    value: 2700,
                    name: '2700 RPM',
                  },
                  {
                    value: 2750,
                    name: '2750 RPM',
                  },
                  {
                    value: 2800,
                    name: '2800 RPM',
                  }
                ]}
                style={speedStyle}
              />
            </div>
          </section>
          )
        : ''}
    </div>
  )
}

Autostart.propTypes = {
  devices: PropTypes.array.isRequired,
}

export default injectIntl(Autostart)
